import 'src/global.css';

// ----------------------------------------------------------------------

import { useEffect } from 'react';

import { Toaster } from 'react-hot-toast';
import { useColorScheme } from '@mui/material/styles';

import { Router } from 'src/routes/router';

import { useScrollToTop } from 'src/hooks/use-scroll-to-top';
import { DebugModeProvider, useDebugMode } from 'src/hooks/use-debug-mode';

import { ThemeProvider } from 'src/theme/theme-provider';

import { MotionLazy } from 'src/components/animate/motion-lazy';
import { ProgressBar } from 'src/components/progress-bar';
import {
  defaultSettings,
  SettingsDrawer,
  SettingsProvider,
  useSettingsContext,
} from 'src/components/settings';

import { AuthProvider } from './auth/AuthContext';

import { GqlProvider } from './GqlProvider';
import DialogTwoFactorRecoveryCodeDownload from './components/dialogs/dialog-2fa-download-recovery-code';

// ----------------------------------------------------------------------

/** Dark mode is debug-only. Outside debug, stay on the light default. */
function EnforceLightMode() {
  const { debugMode } = useDebugMode();
  const settings = useSettingsContext();
  const { mode, setMode } = useColorScheme();

  useEffect(() => {
    if (debugMode) return;
    if (settings.colorScheme !== 'light') {
      settings.onUpdateField('colorScheme', 'light');
    }
    if (mode && mode !== 'light') {
      setMode('light');
    }
  }, [debugMode, mode, setMode, settings]);

  return null;
}

export default function App() {
  useScrollToTop();

  return (
    <AuthProvider>
      <SettingsProvider settings={defaultSettings}>
        <ThemeProvider>
          <GqlProvider>
            <DebugModeProvider>
              <EnforceLightMode />
              <MotionLazy>
                <ProgressBar />
                <SettingsDrawer />
                <Router />
                <DialogTwoFactorRecoveryCodeDownload />
                <Toaster />
              </MotionLazy>
            </DebugModeProvider>
          </GqlProvider>
        </ThemeProvider>
      </SettingsProvider>
    </AuthProvider>
  );
}
